/**
 * Finds the path for an org chart.
 * The path is a specific array AgGrid is looking for in order to display data as a tree.
 * For a given Agent, we need to list in its path the service he belongs to and all the ancestor of his service:
 * For example , the agent Dupont belongs to the service EHPAD. The service EHPAD has a parent orgChart call Pôle hébergement.
 * Agent dupont's path will look like this: ['Pôle Hébergement', 'EHPAD', 'Dupont']
 * @param tree The tree to search in.
 * @param values The value we want the path from
 * @returns The lowest common ancestor of the values, or null if not found.
 */
export function findPath(
    node: TreeT,
    value: string,
    path: string[] = [],
): string[] {
    if (!node) return [];
    if (node.value === value) return path.concat(node.value);

    const result: string[] = node.children.reduce((acc: string[], child) => {
        if (acc.includes(value)) {
            return acc;
        }
        return findPath(child, value, path.concat(node.value));
    }, []);
    return result;
}

/**
 * Returns a flattened version of a tree, containing only the `label` and `value` properties of each node.
 * @param tree - The root node of the tree.
 * @returns An array of objects representing each node in the tree.
 */
export function getFlatTree(tree: TreeT): { label: string; value: string }[] {
    // Initialize an empty array to hold the flattened tree.
    const flatTree: { label: string; value: string }[] = [];

    // Define a recursive function to traverse the tree and add nodes to the flattened tree.
    function traverse(node: TreeT) {
        // Extract the `value` and `label` properties from the current node and add them to the flattened tree.
        const { value, label } = node;
        flatTree.push({ label, value });

        // Recursively traverse the children of the current node.
        node.children.forEach((child: TreeT) => traverse(child));
    }
    traverse(tree);
    return flatTree;
}
